/* ===========================================================================
 *  "Zet als app op je apparaat"
 *
 *  Chrome en Edge (Windows, Android, ChromeOS) sturen `beforeinstallprompt`
 *  als de site installeerbaar is. Dat moment vangen we vroeg op (start() in
 *  main.tsx, vóór React), zodat onze eigen knop het later kan tonen.
 *
 *  Safari (iPhone, iPad, Mac) kent dat niet; daar laat de knop zien hoe het
 *  via het menu gaat. Firefox op de computer kan het niet: geen knop.
 * =========================================================================== */

import { useEffect, useState } from 'react'

interface Voorstel extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let voorstel: Voorstel | null = null
let geinstalleerd = false
const luisteraars = new Set<() => void>()
const meld = () => luisteraars.forEach((f) => f())

export function start() {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    voorstel = e as Voorstel
    meld()
  })
  window.addEventListener('appinstalled', () => { geinstalleerd = true; voorstel = null; meld() })
  if ('serviceWorker' in navigator && window.isSecureContext) {
    window.addEventListener('load', () => { navigator.serviceWorker.register('/sw.js').catch(() => { /* dan geen installeren */ }) })
  }
}

/** Draait hij al als app (los venster of schermvullend)? */
export const alsApp = () =>
  window.matchMedia('(display-mode: standalone), (display-mode: fullscreen), (display-mode: minimal-ui)').matches
  || (navigator as unknown as { standalone?: boolean }).standalone === true

export type Toestel = 'voorstel' | 'iphone' | 'ipad' | 'mac-safari' | 'geen'

function toestel(): Toestel {
  const ua = navigator.userAgent
  if (voorstel) return 'voorstel'
  if (/iPhone|iPod/.test(ua)) return 'iphone'
  if (/iPad/.test(ua) || (ua.includes('Macintosh') && 'ontouchend' in document)) return 'ipad'
  if (ua.includes('Macintosh') && /Version\/1[7-9]|Version\/[2-9]\d/.test(ua) && !/Chrome|Chromium|Edg|Firefox/.test(ua)) return 'mac-safari'
  return 'geen'
}

export function useInstalleren() {
  const [, zet] = useState(0)
  useEffect(() => {
    const f = () => zet((n) => n + 1)
    luisteraars.add(f)
    return () => { luisteraars.delete(f) }
  }, [])
  const soort = geinstalleerd || alsApp() ? 'geen' : toestel()
  return {
    soort,
    async installeer(): Promise<boolean> {
      if (!voorstel) return false
      await voorstel.prompt()
      const keuze = await voorstel.userChoice
      voorstel = null
      meld()
      return keuze.outcome === 'accepted'
    },
  }
}

/** Het manifest bij deze pagina: klant, beheer of de tablet van een vestiging. */
export function zetManifest(pad: string) {
  const eerste = pad.split('/')[1]?.toLowerCase() ?? ''
  const href = !eerste ? '/manifest.webmanifest' : `/manifest/${eerste.replace(/[^a-z0-9]/g, '')}.webmanifest`
  let l = document.getElementById('manifest') as HTMLLinkElement | null
  if (!l) {
    l = document.createElement('link')
    l.id = 'manifest'
    l.rel = 'manifest'
    document.head.appendChild(l)
  }
  if (l.getAttribute('href') !== href) l.href = href
}
